import { useAppSelector } from 'src/utils/hooks';
import CheckboxQuestion from './questions/CheckboxQuestion';
import ComboboxQuestion from './questions/ComboboxQuestion';
import DateQuestion from './questions/DateQuestion';
import LabelQuestion from './questions/LabelQuestion';
import NumberQuestion from './questions/NumberQuestion';
import OccupationQuestion from './questions/OccupationQuestion';
import RadioQuestion from './questions/RadioQuestion';
import TextQuestion from './questions/TextQuestion';
import DownloadButton from './questions/DownloadButton';
import OrderQuestion from './questions/OrderQuestion';
import {
  getDocumentById,
  extractDocumentId,
  downloadDocument,
  resolveMandant,
} from 'src/utils';
import { Button, Divider } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import { blindModeOverrides } from 'src/config/blindModeOverrides';

interface Props {
  questions: Question[];
  personId: string;
  riskIdx: number;
  personIdx: number;
  formRef: any;
  readonly: boolean;
  validateForm: any;
}
export default function ListQuestions({
  questions,
  personId,
  riskIdx,
  personIdx,
  formRef,
  readonly,
  validateForm,
}: Props) {
  const { errors, touched, setFieldValue } = useFormikContext();
  const [downloadAllDisable, setDownloadAllDisable] = useState(false);
  const { isColorBlindMode } = useAppSelector((state) => state.config);
  const { steps } = useAppSelector((state) => state.stepper);
  useEffect(() => {
    if (formRef.current) {
      for (const fieldName in errors) {
        if (
          touched[fieldName as keyof typeof touched] &&
          errors[fieldName as keyof typeof errors]
        ) {
          const fieldElement = formRef.current.querySelector(
            `[name="${fieldName}"]`,
          );
          if (fieldElement) {
            fieldElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            break;
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, touched]);

  const documents: DownloadDoc[] = useAppSelector(
    (state) => state.riskCheck.documents,
  );

  function renderQuestion(
    q: Question,
    index: number,
  ): JSX.Element | null | undefined {
    const question = JSON.parse(JSON.stringify(q));
    const defaultMandant = process.env.REACT_APP_MANDANT_DEFAULT as string;

    const currentMandantKey = resolveMandant(defaultMandant);
    const mandantOverrides = (blindModeOverrides as Record<string, any>)[
      currentMandantKey
    ];

    if (isColorBlindMode && mandantOverrides) {
      const override = (mandantOverrides.questions as Record<string, any>)[
        question.key
      ];

      if (override?.text) {
        question.text = override.text;
        if (override.html) {
          question.html = true;
        }
      }

      if (
        question.answer.type === 'Radio' &&
        question.answer.picks?.length === 1
      ) {
        const overridePicks = mandantOverrides.picks?.default ?? [];
        question.answer.picks = question.answer.picks.map(
          (pick: { name: string }) => {
            const pickOverride = overridePicks.find(
              (p: { original: string }) => p.original === pick.name,
            );
            return pickOverride
              ? { ...pick, name: pickOverride.replacement }
              : pick;
          },
        );
      }
    }

    const key = `${question.key}${index}`;
    if (
      (question.computed && question.expComputation !== null) ||
      question.predefine ||
      !question.visible
    )
      return null;

    if (question.answer.type === 'Text') {
      if (question.key === 'orderIban') {
        return (
          <OrderQuestion
            key={key}
            question={question}
            riskIdx={riskIdx}
            personIdx={personIdx}
          />
        );
      }
      return <TextQuestion key={key} question={question} />;
    } else if (question.answer.type === 'Radio') {
      const documentId = extractDocumentId(question.key);
      const document = getDocumentById(documentId, documents);
      if (
        question.key.match(/downloadLnDocument_(.+)/) ||
        question.key.match(/lnTaaDownloadDocuments_(.+)/)
      ) {
        return (
          <DownloadButton
            question={question}
            downloadDocument={() => {
              document &&
                downloadDocument(
                  document?.content,
                  `${document.displayName}.${document.fileType}`,
                );
            }}
          />
        );
      }

      return question.answer.picks?.length === 1 ? (
        <CheckboxQuestion
          key={key}
          question={question}
          riskIdx={riskIdx}
          personIdx={personIdx}
          isColorBlindMode={isColorBlindMode}
        />
      ) : (
        <RadioQuestion
          key={key}
          question={question}
          riskIdx={riskIdx}
          personIdx={personIdx}
          validateForm={validateForm}
        />
      );
    } else if (question.answer.type === 'Combobox') {
      return question.answer.picks?.length === 0 ? (
        <OccupationQuestion key={key} question={question} person={personId} />
      ) : (
        <ComboboxQuestion
          key={key}
          question={question}
          riskIdx={riskIdx}
          personIdx={personIdx}
        />
      );
    } else if (
      question.answer.type === 'Number' ||
      question.answer.type === 'NumberInteger' ||
      question.answer.type === 'Currency'
    ) {
      return (
        <NumberQuestion
          key={key}
          question={question}
          riskIdx={riskIdx}
          personIdx={personIdx}
        />
      );
    } else if (question.answer.type === 'Date') {
      return (
        <DateQuestion
          key={key}
          question={question}
          answer={question.answer.value}
        />
      );
    } else {
      return <LabelQuestion key={key} question={question} />;
    }
  }

  function renderChildQuestions(q: Question): JSX.Element | null {
    if (q?.childQuestionary) {
      return q.childQuestionary.map((child: any) => {
        return (
          <ListQuestions
            readonly={readonly}
            questions={child?.questions}
            personId={personId}
            riskIdx={riskIdx}
            personIdx={personIdx}
            formRef={formRef}
            validateForm={validateForm}
          />
        );
      });
    }
    return null;
  }

  const documentIds = questions
    .filter((q) => q.key.match(/downloadLnDocument_(.+)/))
    .map((q) => q.key);

  const handleDownloadAll = () => {
    let count = 1;
    documentIds.forEach((id) => {
      const documentId = extractDocumentId(id);
      const document = getDocumentById(documentId, documents);
      setTimeout(() => {
        downloadDocument(
          document?.content,
          `${document?.displayName}.${document?.fileType}`,
        );
      }, 250 * count);
      count++;
      setDownloadAllDisable(true);
      setFieldValue(id, 'true');
    });
    setTimeout(() => {
      validateForm();
    }, 500);
  };

  return (
    <>
      {questions.map((q, index) => {
        const key = `${q.key}${index}`;
        return (
          <React.Fragment key={key}>
            {renderQuestion(q, index)}
            {renderChildQuestions(q)}
          </React.Fragment>
        );
      })}
      {documentIds.length > 1 && !readonly && (
        <>
          <Divider
            sx={{ borderColor: isColorBlindMode ? '#000000' : undefined }}
          />

          <Button
            variant="contained"
            sx={
              isColorBlindMode
                ? {
                    backgroundColor: 'accessibility.button.main',
                    color: 'accessibility.button.contrastText',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2.5px',
                    marginY: 2,
                    border: (theme) =>
                      `1px solid ${theme.palette.accessibility?.button?.border}`,
                    '&:hover': {
                      backgroundColor: '#222',
                      color: 'accessibility.button.contrastText',
                      textDecoration: 'underline',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'text.disabled',
                      color: 'accessibility.button.contrastText',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2.5px',
                    },
                  }
                : { marginY: 2 }
            }
            color="primary"
            disabled={downloadAllDisable}
            onClick={handleDownloadAll}
          >
            ALLE DOKUMENTE HERUNTERLADEN
          </Button>
        </>
      )}
    </>
  );
}
